import React, { Fragment, useState } from 'react';
import EditPartnerFooter from './edit-partner-footer';
import { ReactComponent as EditIcon } from 'assets/icons/edit-blue.svg';
import { toast } from 'react-toastify';
import { BaseModal, ToastMessage, formatPhoneNumber } from '@cokitchen/cokitchen-components';
import { PartnerType } from 'models/partner.model';
import { PartnerFormType } from 'components/logistics/partners/steps/types';
import { apiService } from 'utils/api';
import PartnerFirstStep from 'components/logistics/partners/steps/partner-first-step';
import PartnerSecondStep from 'components/logistics/partners/steps/partner-second-step';

type EditPartnerDetailsProps = {
  show: boolean;
  closeModal: () => void;
  partner: PartnerType;
};

const EditPartnerDetails = ({ show, closeModal, partner }: EditPartnerDetailsProps) => {
  const emptyForm = {
    partner_name: partner.name,
    partner_email: partner.contact_email,
    mobile_no: partner.contact_phone_number,
    address: partner.address,
    account_number: partner.payout_account_number,
    account_name: partner.payout_account_name,
    bank_name: partner.payout_bank_name,
    location: partner.locations_served?.map((polygon) => polygon.id)
  };
  const [bankName, setBankName] = useState([partner.payout_bank_name]);

  const [formData, setFormData] = useState(emptyForm as unknown as PartnerFormType);
  const [location, setLocation] = useState<string[]>(emptyForm.location);
  const [loading, setLoading] = useState(false);
  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };
  const [step, setStep] = useState(1);
  const firstStepNotValid = () => {
    if (!formData.partner_email) return false;
    return (
      formData.partner_name?.length === 0 ||
      formData.partner_email?.length === 0 ||
      formData.mobile_no?.length === 0 ||
      formData.address?.length === 0 ||
      location.length === 0
    );
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (step === 1) {
        await apiService.post(`logistics/update-partner/${partner.id}`, {
          data: {
            contact_phone_number: formatPhoneNumber(formData.mobile_no),
            name: formData.partner_name,
            address: formData.address,
            contact_email: formData.partner_email,
            locations_served: location
          },
          baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
        });
        setStep((prev) => prev + 1);
      } else if (step === 2) {
        await apiService.post(`logistics/update-partner/${partner.id}`, {
          data: {
            payout_account_number: formData.account_number,
            payout_account_name: formData.account_name,
            payout_bank_name: bankName[1]
          },
          baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
        });
        closeModal();
        toast(<ToastMessage type='success' message='Updated successfully' />);
      }
    } catch {
      console.log('error');
    } finally {
      setLoading(false);
    }
  };

  const stepIsDisabled = () => {
    if (step === 1) {
      return firstStepNotValid();
    }
  };

  return (
    <BaseModal
      position='right'
      isOpen={show}
      onRequestClose={() => {
        closeModal();
      }}
      hasBackButton={step >= 2}
      onBackButton={() => setStep(step - 1)}
      title='Edit Partner Details'
      titleIcon={<EditIcon />}
      footer={
        <EditPartnerFooter
          step={step}
          onClickNext={() => handleSubmit()}
          onClickSkip={() => setStep(step + 1)}
          disabled={!!stepIsDisabled()}
          loading={loading}
        />
      }>
      <form onSubmit={(ev) => ev.preventDefault()}>
        {step === 1 && (
          <Fragment>
            <PartnerFirstStep
              handleChange={handleChange}
              location={location}
              setLocation={setLocation}
              formData={formData}
            />
          </Fragment>
        )}
        {step === 2 && (
          <PartnerSecondStep
            handleChange={handleChange}
            bankName={bankName}
            setBankName={setBankName}
            formData={formData}
          />
        )}
      </form>
    </BaseModal>
  );
};

export default EditPartnerDetails;
